import { Domains, getLocale, Locale, stripLeadingSlashes } from '@libs/core';
import type { Metadata } from 'next';

const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME;

type DynamicMetadataProps = {
    pathname?: string | string[];
    metaTags?: Metadata;
};

function buildCanonicalOrAlternateUrl(initialUrl: string, domain: string) {
    if (initialUrl.startsWith('http')) {
        return initialUrl;
    }

    if (initialUrl === '/') {
        return domain;
    }

    const pathname = stripLeadingSlashes(initialUrl);

    /**
     * If we cannot generate a canonical dynamically, it is best to leave it blank
     * as an incorrect canonical is more harmful to SEO than a blank one!
     */
    return pathname ? `${domain}/${pathname}` : '';
}

export const getStaticViewport = {
    themeColor: '#ffffff',
};

/**
 * DO NOT include "manifest" in this configuration!
 *
 * See comment in @libs/shared-ui/src/server/exports/layouts/PageLayout.tsx
 */
export const getStaticMetadata = {
    icons: APP_NAME ? `/${APP_NAME}/favicon.ico` : '/favicon.ico',
    // Default title and description below can be overridden by page
    title: 'Pet Supplies, Accessories, and Pet Food - Pet Stores | PetSmart',
    description: `PetSmart pet stores offer quality pet products, pet food, and accessories. Find pet service locations for pet grooming, dog training, and boarding.`,
};

export const getDynamicMetadata = ({ pathname = '', metaTags = {} }: DynamicMetadataProps = {}) => {
    const stringPathname = Array.isArray(pathname) ? pathname.join('/') : pathname;

    // Create canonical url
    // Display production domain for all environments
    const canonical = buildCanonicalOrAlternateUrl(
        metaTags.alternates?.canonical?.toString() ?? stringPathname,
        Domains[getLocale()]
    );

    // Generate alternates for all languages
    const languages = Object.values(Locale).map(locale => ({
        [locale]: buildCanonicalOrAlternateUrl(stringPathname, Domains[locale]),
    }));

    return {
        ...getStaticMetadata,
        ...metaTags,
        alternates: {
            languages: Object.assign({}, ...languages),
            ...metaTags.alternates,
            canonical,
        },
    };
};
